import {
  Bearing,
  Component,
  ComponentSide,
  GableStuddingDirection,
  HeelReinforcementType,
  isProfileTruss,
  isTruss,
  Lumber,
  LumberFilter,
  MemberType,
  StubDistanceOption,
} from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';

import { getDefaultGableSettings } from '../../library/profiles/profileTrussDefaults';
import { duplicateActiveComponentDesign } from '../../store/actions/appActions';
import { deleteBearing, deleteItems } from '../../store/actions/deletionActions';
import {
  setBearingWidthAbsoluteGeometry,
  setBearingWidthRelativeGeometry,
} from '../../store/actions/editBearingActions';
import { setComponentName, setProfileTrussHeelReinforcement } from '../../store/actions/editComponentActions';
import { gable, replaceLumbers, stub } from '../../store/actions/editorActions';
import { insertBearing, startInsertBearing } from '../../store/actions/insertActions';
import {
  __UNSAFE_loadProfileDefaultsUsingProjectGuidOrFallback,
  loadMaterialSettings,
} from '../../store/actions/loadActions';
import { updateComponentDesignQuantity } from '../../store/actions/projectActions';
import { saveComponentMaterialSettings } from '../../store/actions/settingsActions';
import { calculateBearingInsertionInfo } from '../../store/selectors/bearingInsertionSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getCommittedComponentDesigns } from '../../store/selectors/getComponentDesignsSelectors';
import { getUniqueLumbersInLumberListForActiveComponentDesign } from '../../store/selectors/getUniqueLumbersInLumberList';
import { clearInsertingObject } from '../../store/slices/applicationSlice';
import { getLumberPropertySetKey } from '../../store/slices/lumberPropertySetsSlice';
import {
  describeBearingGeometry,
  describeLumber,
  describeMemberGeometry,
  findTrussByName,
  getTrussMembers,
  openTruss,
  optionalNumber,
  requireNumber,
  requireString,
  requireStringArray,
  summarizeTruss,
  waitForAppToSettle,
} from './helpers';
import { chooseLumber, parseGrade, parseSpecies } from './trussTools';
import { defineTool, ToolError } from './types';

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };
const BEARING_TOLERANCE_IN_INCHES = 1e-6;
const HEEL_REINFORCEMENT_TYPES: readonly HeelReinforcementType[] = [
  'None',
  'Wedge',
  'TopChordSlider',
  'BottomChordSlider',
];

/**
 * Finds where a bearing can go on a truss's bottom at x, the way the app offers a snap there when the designer
 * inserts a bearing, so the bearing is defined the same way a click would define it.
 */
export function findBottomBearingSnap(snaps: ReturnType<typeof calculateBearingInsertionInfo>, x: number) {
  for (const snap of snaps) {
    if (snap.trussSide !== 'Bottom' || snap.type !== 'segment') continue;
    const { basePoint, endPoint } = snap.item;
    const minX = Math.min(basePoint.x, endPoint.x);
    const maxX = Math.max(basePoint.x, endPoint.x);
    if (x < minX - BEARING_TOLERANCE_IN_INCHES || x > maxX + BEARING_TOLERANCE_IN_INCHES) continue;
    const t = endPoint.x === basePoint.x ? 0 : (x - basePoint.x) / (endPoint.x - basePoint.x);
    return { snap, point: new Point2D(x, basePoint.y + t * (endPoint.y - basePoint.y)) };
  }
  return null;
}

function findBearing(bearings: readonly Bearing[], name: string) {
  const bearing = bearings.find((candidate) => candidate.name.toLowerCase() === name.trim().toLowerCase());
  if (bearing == null)
    throw new ToolError(`There is no bearing "${name}". The bearings are ${bearings.map((b) => b.name).join(', ')}.`);
  return bearing;
}

// Where each bearing landed, since add_bearing snaps to the bottom chord.
function describeBearings(component: Component) {
  return component.bearings.map((bearing) => ({
    name: bearing.name,
    ...describeBearingGeometry(bearing, component),
    width:
      bearing.relativeGeometry != null
        ? 'width' in bearing.relativeGeometry
          ? bearing.relativeGeometry.width
          : null
        : bearing.width,
    type: bearing.bearingType,
  }));
}

export const trussManagementTools = [
  defineTool<{ truss: string; at: number; width?: number }>({
    name: 'add_bearing',
    description:
      'Adds a bearing under a truss’s bottom chord at x inches from its left end, as Insert > Bearing does, and sets ' +
      'its width (inches; defaults to 3.5). The app names it with the next letter. Run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, at: { type: 'number' }, width: { type: 'number' } },
      required: ['truss', 'at'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isTruss(componentDesign.component)) throw new ToolError('Only trusses take bearings.');
      const at = requireNumber(input, 'at');
      const width = optionalNumber(input, 'width');
      if (width != null && width <= 0) throw new ToolError('Width must be more than 0.');

      const found = findBottomBearingSnap(calculateBearingInsertionInfo(componentDesign.component), at);
      if (found == null) throw new ToolError(`A bearing can’t go under the bottom chord at x = ${at}.`);

      const bearingsBefore = new Set(componentDesign.component.bearings.map((bearing) => bearing.guid));
      dispatch(startInsertBearing());
      try {
        // The snap's metadata says how the bearing sits against the chord, as a click on that snap would.
        const { type: _type, item: _item, ...metadata } = found.snap;
        dispatch(insertBearing({ bearingDefinitionPoint: found.point, ...metadata }));
      } finally {
        dispatch(clearInsertingObject());
      }

      const added = getActiveComponentDesign(getState())!.component.bearings.find(
        (bearing) => !bearingsBefore.has(bearing.guid),
      );
      if (added == null) throw new ToolError('The app could not add the bearing.');
      if (width != null) dispatch(setBearingWidthRelativeGeometry(added, componentDesign.guid, width));
      await waitForAppToSettle(getState);

      return {
        summary: {
          added: added.name,
          bearings: describeBearings(getActiveComponentDesign(getState())!.component),
        },
      };
    },
  }),

  defineTool<{ truss: string; bearing: string; width: number }>({
    name: 'edit_bearing',
    description: 'Changes a truss bearing’s width in inches, as its properties panel does.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, bearing: { type: 'string' }, width: { type: 'number' } },
      required: ['truss', 'bearing', 'width'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const bearing = findBearing(componentDesign.component.bearings, requireString(input, 'bearing'));
      const width = requireNumber(input, 'width');
      if (width <= 0) throw new ToolError('Width must be more than 0.');

      dispatch(
        bearing.relativeGeometry
          ? setBearingWidthRelativeGeometry(bearing, componentDesign.guid, width)
          : setBearingWidthAbsoluteGeometry(bearing, componentDesign.guid, width),
      );
      await waitForAppToSettle(getState);
      return { summary: { bearings: describeBearings(getActiveComponentDesign(getState())!.component) } };
    },
  }),

  defineTool<{ truss: string; bearing: string }>({
    name: 'delete_bearing',
    description: 'Deletes a bearing from a truss.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, bearing: { type: 'string' } },
      required: ['truss', 'bearing'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const bearing = findBearing(componentDesign.component.bearings, requireString(input, 'bearing'));
      await dispatch(deleteBearing(componentDesign, bearing));
      await waitForAppToSettle(getState);

      const { component } = getActiveComponentDesign(getState())!;
      if (component.bearings.some((candidate) => candidate.guid === bearing.guid))
        throw new ToolError(`The app could not delete bearing ${bearing.name}.`);
      return { summary: { bearings: describeBearings(component) } };
    },
  }),

  defineTool<{ truss: string; side: ComponentSide; distance: number; measure?: 'stub' | 'remaining' }>({
    name: 'stub_truss',
    description:
      'Stubs a truss on one side, as Edit Truss > Stub does. By default `distance` is how much to cut off; with ' +
      'measure "remaining" it is the span to keep. Inches.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        side: { type: 'string', enum: ['Left', 'Right'] },
        distance: { type: 'number' },
        measure: { type: 'string', enum: ['stub', 'remaining'] },
      },
      required: ['truss', 'side', 'distance'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (input.side !== 'Left' && input.side !== 'Right') throw new ToolError('"side" must be Left or Right.');
      const distance = requireNumber(input, 'distance');
      if (distance <= 0 || distance >= componentDesign.component.span)
        throw new ToolError(`The distance must be between 0 and the truss’s span, ${componentDesign.component.span}.`);

      const option: StubDistanceOption = input.measure === 'remaining' ? 'RemainingSpan' : 'DistanceToStub';
      const stubbed = await dispatch(stub(input.side, option, distance));
      if (stubbed == null) throw new ToolError(`The app could not stub ${componentDesign.component.name}.`);
      await waitForAppToSettle(getState);
      return { summary: summarizeTruss(getState().componentDesigns.data[componentDesign.guid]) };
    },
  }),

  defineTool<{
    truss: string;
    side: ComponentSide;
    type: HeelReinforcementType;
    distance?: number;
    size?: string;
    species?: string;
    grade?: string;
  }>({
    name: 'set_heel_reinforcement',
    description:
      'Reinforces one heel of a roof truss, as Edit Truss > Adjust Heels does: a wedge, a top chord slider, or a ' +
      'bottom chord slider, or None to take one off. Drawings list sliders under LUMBER, such as "SLIDER Left 2x6 SP ' +
      'No.3 2-6-0"; the length there is `distance`, in inches, how far the slider reaches in from the truss’s end. ' +
      'Give the slider’s size, species, and grade here, not with set_lumber afterward: the slider is fitted to its ' +
      'lumber, and a wider board swapped in later overlaps the end vertical. Wedges take no distance. Returns the ' +
      'truss, with the slider or wedge among its members.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        side: { type: 'string', enum: ['Left', 'Right'] },
        type: { type: 'string', enum: HEEL_REINFORCEMENT_TYPES },
        distance: { type: 'number' },
        size: { type: 'string', description: 'The slider’s nominal size, such as 2x6.' },
        species: { type: 'string', description: 'For example SP, SPF, or Southern_Pine.' },
        grade: { type: 'string', description: 'For example No.3 or Number_3.' },
      },
      required: ['truss', 'side', 'type'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isProfileTruss(componentDesign.component))
        throw new ToolError('Only profile (roof) trusses have heels to reinforce.');
      if (input.side !== 'Left' && input.side !== 'Right') throw new ToolError('"side" must be Left or Right.');
      if (!HEEL_REINFORCEMENT_TYPES.includes(input.type))
        throw new ToolError(`"type" must be one of ${HEEL_REINFORCEMENT_TYPES.join(', ')}.`);
      const isSlider = input.type === 'TopChordSlider' || input.type === 'BottomChordSlider';
      const distance = isSlider ? requireNumber(input, 'distance') : null;
      if (distance != null && (distance <= 0 || distance >= componentDesign.component.span / 2))
        throw new ToolError('A slider’s distance must be more than 0 and less than half the span.');

      // The app builds a slider from the truss's slider lumber, so set that first and the slider fits its board.
      if (isSlider && (input.size != null || input.species != null || input.grade != null)) {
        const settings = await dispatch(loadMaterialSettings(componentDesign.guid));
        const current = settings.profileMaterialSettings;
        const chord = getTrussMembers(componentDesign).find((member) => member.memberType === 'TopChord');
        if (current == null || chord == null) throw new ToolError('Could not read this truss’s material settings.');
        const lumber = chooseLumber(
          { ...chord, lumber: current.sliderLumber },
          input,
          getUniqueLumbersInLumberListForActiveComponentDesign(getState()),
        );
        if (getLumberPropertySetKey(lumber) !== getLumberPropertySetKey(current.sliderLumber))
          await dispatch(
            saveComponentMaterialSettings(componentDesign.guid, {
              ...settings,
              profileMaterialSettings: { ...current, sliderLumber: lumber },
            }),
          );
      }

      await dispatch(setProfileTrussHeelReinforcement(componentDesign.guid, input.type, input.side, distance));
      await waitForAppToSettle(getState);

      // Wedges and sliders on this side, the half of the truss nearer its end.
      const middle = componentDesign.component.span / 2;
      const reinforcementsOnSide = () =>
        getState().componentDesigns.data[componentDesign.guid].component.members.filter((member) => {
          if (member.memberType !== 'Wedge' && member.memberType !== 'Slider') return false;
          const centerline = describeMemberGeometry(
            member,
            getState().componentDesigns.data[componentDesign.guid].component,
          ).centerline;
          if (centerline == null) return false;
          const x = (centerline.from.x + centerline.to.x) / 2;
          return input.side === 'Left' ? x < middle : x > middle;
        });
      const remaining = reinforcementsOnSide();
      if (input.type === 'None' && remaining.length > 0) {
        // The app leaves a wedge or slider whose lumber was changed; take it off as deleting it would.
        await dispatch(deleteItems(remaining.map((member) => ({ type: 'member' as const, item: member }))));
        await waitForAppToSettle(getState);
        if (reinforcementsOnSide().length > 0)
          throw new ToolError(`The app couldn’t take ${remaining.map((member) => member.name).join(', ')} off.`);
      } else if (input.type !== 'None' && remaining.length === 0)
        throw new ToolError(
          `The app didn’t add a ${input.type} at the ${input.side.toLowerCase()} heel. Check its notifications for why.`,
        );
      return { summary: summarizeTruss(getState().componentDesigns.data[componentDesign.guid]) };
    },
  }),

  defineTool<{
    truss: string;
    studSpacing?: number;
    direction?: GableStuddingDirection;
    structural?: boolean;
  }>({
    name: 'make_gable',
    description:
      'Makes a truss a gable end, as Edit Truss > Make Gable does, starting from the project’s gable defaults. Stud ' +
      'spacing is in inches.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        studSpacing: { type: 'number' },
        direction: { type: 'string', enum: ['LeftToRight', 'RightToLeft', 'LocationOutward'] },
        structural: { type: 'boolean', description: 'A structural gable carries roof load like a common truss.' },
      },
      required: ['truss'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isTruss(componentDesign.component)) throw new ToolError('Only trusses can be gables.');

      const defaults = await dispatch(__UNSAFE_loadProfileDefaultsUsingProjectGuidOrFallback());
      const settings = getDefaultGableSettings(defaults, {
        isGable: true,
        ...(input.studSpacing != null ? { studSpacing: input.studSpacing } : {}),
        ...(input.direction != null ? { studdingDirection: input.direction } : {}),
        ...(input.structural != null ? { isStructuralGable: input.structural } : {}),
      });
      await dispatch(gable(settings));
      await waitForAppToSettle(getState);

      const updated = getState().componentDesigns.data[componentDesign.guid];
      if (!isTruss(updated.component) || !updated.component.gableSettings.isGable)
        throw new ToolError(`The app could not make ${componentDesign.component.name} a gable.`);
      return { summary: { ...summarizeTruss(updated), gable: updated.component.gableSettings } };
    },
  }),

  defineTool<{ truss: string; name?: string; quantity?: number }>({
    name: 'set_truss_properties',
    description:
      'Renames a truss (its mark, such as T01 or G1) or sets how many of it the job needs. A truss placed in the ' +
      'layout gets its quantity from its envelopes instead.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, name: { type: 'string' }, quantity: { type: 'number' } },
      required: ['truss'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = findTrussByName(getState(), requireString(input, 'truss'));
      if (input.name == null && input.quantity == null) throw new ToolError('Give a name or a quantity.');

      if (input.name != null) {
        const name = input.name.trim();
        if (name === '') throw new ToolError('The name can’t be empty.');
        const taken = getCommittedComponentDesigns(getState()).some(
          (other) => other.guid !== componentDesign.guid && other.component.name.toLowerCase() === name.toLowerCase(),
        );
        if (taken) throw new ToolError(`Another truss is already named "${name}".`);
        dispatch(setComponentName(name, componentDesign.guid));
      }
      if (input.quantity != null) {
        if (!Number.isInteger(input.quantity) || input.quantity < 1) throw new ToolError('Quantity must be 1 or more.');
        const envelopeCount = Object.values(getState().layout.trussEnvelopes).filter(
          (envelope) => envelope.componentDesignGuid === componentDesign.guid,
        ).length;
        if (envelopeCount > 0)
          throw new ToolError(
            `${componentDesign.component.name} is placed in the layout, so its quantity is the number of envelopes it ` +
              `sits in (${envelopeCount}). Add or remove truss envelopes to change it.`,
          );
        await dispatch(updateComponentDesignQuantity(componentDesign.guid, input.quantity));
      }
      await waitForAppToSettle(getState);

      return {
        summary: {
          name: getState().componentDesigns.data[componentDesign.guid].component.name,
          ...(input.quantity != null ? { quantity: input.quantity } : {}),
        },
      };
    },
  }),

  defineTool<{ truss: string; name?: string }>({
    name: 'duplicate_truss',
    description:
      'Copies a truss with its settings and jack connections, as the Duplicate button does, and opens the copy. ' +
      'Give the copy a name, or the app names it after the original.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, name: { type: 'string', description: 'The copy’s name.' } },
      required: ['truss'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const original = await openTruss(dispatch, getState, requireString(input, 'truss'));
      await dispatch(duplicateActiveComponentDesign());
      await waitForAppToSettle(getState);

      const copy = getActiveComponentDesign(getState());
      if (copy == null || copy.guid === original.guid) throw new ToolError('The app could not duplicate the truss.');
      if (input.name != null && input.name.trim() !== '') {
        dispatch(setComponentName(input.name.trim(), copy.guid));
        await waitForAppToSettle(getState);
      }
      return { summary: { copy: getState().componentDesigns.data[copy.guid].component.name } };
    },
  }),

  defineTool<{
    trusses?: string[];
    where?: { memberType?: MemberType; size?: string; species?: string; grade?: string };
    to: { size?: string; species?: string; grade?: string };
  }>({
    name: 'replace_lumber',
    description:
      'Replaces lumber across trusses, as the Replace Lumber panel does: every member matching `where` changes to ' +
      '`to`, and the changed members are locked. Defaults to every truss. Sizes look like 2x6; species and grades ' +
      'like SYP and #2. Run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        trusses: { type: 'array', items: { type: 'string' } },
        where: {
          type: 'object',
          properties: {
            memberType: { type: 'string', enum: ['TopChord', 'BottomChord', 'Web'] },
            size: { type: 'string' },
            species: { type: 'string' },
            grade: { type: 'string' },
          },
        },
        to: {
          type: 'object',
          properties: { size: { type: 'string' }, species: { type: 'string' }, grade: { type: 'string' } },
        },
      },
      required: ['to'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesigns =
        input.trusses != null
          ? requireStringArray(input, 'trusses').map((name) => findTrussByName(getState(), name))
          : getCommittedComponentDesigns(getState()).filter((componentDesign) => isTruss(componentDesign.component));
      if (componentDesigns.length === 0) throw new ToolError('There are no trusses to change.');

      const filter = buildLumberFilter(input.where ?? {});
      const targetPatch = buildLumberPatch(input.to ?? {});
      if (Object.keys(targetPatch).length === 0) throw new ToolError('Say what to change the lumber to.');

      // The new lumber is the matching members' lumber with the changes applied; take it from the first match.
      const example = componentDesigns
        .flatMap((componentDesign) => componentDesign.component.members)
        .find((member) => lumberMatchesFilter(member.lumber, member.memberType, filter));
      if (example == null) throw new ToolError('No member in those trusses matches that lumber.');

      const lumberBefore = new Map(
        componentDesigns.flatMap(({ component }) =>
          component.members.map((member) => [member.guid, describeLumber(member)] as const),
        ),
      );
      await dispatch(
        replaceLumbers(
          componentDesigns.map(({ guid }) => guid),
          { filter, newLumber: { ...example.lumber, ...targetPatch } },
        ),
      );
      await waitForAppToSettle(getState);

      // Read each truss back, so the result shows which members took the new lumber and which trusses had none to change.
      const results = componentDesigns.map(({ guid, component }) => {
        const after = getState().componentDesigns.data[guid]?.component ?? component;
        const changedMembers = after.members
          .filter((member) => lumberBefore.has(member.guid) && lumberBefore.get(member.guid) !== describeLumber(member))
          .map((member) => ({ member: member.name, from: lumberBefore.get(member.guid), to: describeLumber(member) }));
        return { truss: after.name, changed: changedMembers };
      });
      return {
        summary: {
          changed: results.filter((result) => result.changed.length > 0),
          unchanged: results.filter((result) => result.changed.length === 0).map((result) => result.truss),
        },
      };
    },
  }),
];

function parseSize(size: string) {
  const match = size.match(/^\s*(\d+)\s*x\s*(\d+)\s*$/i);
  if (match == null) throw new ToolError(`"${size}" isn’t a lumber size like 2x6.`);
  return { nominalThickness: match[1], nominalWidth: match[2] };
}

export function buildLumberFilter(where: {
  memberType?: MemberType;
  size?: string;
  species?: string;
  grade?: string;
}): LumberFilter {
  return {
    ...(where.memberType != null ? { memberType: where.memberType } : {}),
    ...(where.size != null ? parseSize(where.size) : {}),
    ...(where.species != null ? { species: parseSpecies(where.species) } : {}),
    ...(where.grade != null ? { grade: parseGrade(where.grade) } : {}),
  };
}

export function buildLumberPatch(to: { size?: string; species?: string; grade?: string }): Partial<Lumber> {
  return {
    ...(to.size != null ? parseSize(to.size) : {}),
    ...(to.species != null ? { species: parseSpecies(to.species) } : {}),
    ...(to.grade != null ? { grade: parseGrade(to.grade) } : {}),
  };
}

export function lumberMatchesFilter(lumber: Lumber, memberType: MemberType, filter: LumberFilter) {
  return (
    (filter.memberType == null || filter.memberType === memberType) &&
    (filter.nominalThickness == null || filter.nominalThickness === lumber.nominalThickness) &&
    (filter.nominalWidth == null || filter.nominalWidth === lumber.nominalWidth) &&
    (filter.species == null || filter.species === lumber.species) &&
    (filter.grade == null || filter.grade === lumber.grade)
  );
}
